import React, { useCallback, useEffect, useState } from 'react';
import { X, Pause, Play, ChevronLeft, ChevronRight, AlertTriangle, Pin } from 'lucide-react';
import { Dial, STATUS_COLOURS } from './Dial';
import { fmtValue } from './MetricTile';

function BigTile({ tile }) {
  const colour = STATUS_COLOURS[tile.status] || STATUS_COLOURS.neutral;
  const diff = tile.previous != null && tile.value != null ? Number(tile.value) - Number(tile.previous) : null;
  return (
    <div className="rounded-2xl p-5 flex flex-col justify-between" style={{ background: 'rgba(255,255,255,0.06)', borderTop: `6px solid ${colour}` }} data-testid={`wall-tile-${tile.key}`}>
      <div className="text-base md:text-lg uppercase tracking-wider opacity-80">{tile.label}</div>
      {tile.kind === 'gauge'
        ? <div className="flex justify-center my-2"><Dial value={tile.value} status={tile.status} size={200} unit={tile.unit} label={tile.label} dark /></div>
        : <div className="font-title leading-none my-2" style={{ fontSize: 'clamp(3rem, 7vw, 6.5rem)', color: colour === STATUS_COLOURS.neutral ? 'var(--cream)' : colour }}>{fmtValue(tile)}</div>}
      <div className="text-sm md:text-base opacity-70">
        {diff == null ? 'no trend yet' : Math.abs(diff) < 0.005 ? 'steady vs last week' : `${diff > 0 ? '▲' : '▼'} ${tile.kind === 'money' ? '$' : ''}${Math.abs(diff) % 1 ? Math.abs(diff).toFixed(1) : Math.abs(diff)} vs last week`}
      </div>
    </div>
  );
}

// Full-screen, auto-cycling display for a TV: overview → pinned → each area.
export function WallDisplay({ data, areas, seconds, onClose }) {
  const slides = [{ key: 'overview', label: 'Needs attention' }];
  if (data.prefs?.pinned?.length) slides.push({ key: 'pinned', label: 'Pinned readings' });
  areas.forEach((a) => slides.push({ key: a.key, label: a.label }));
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0);
  const next = useCallback(() => setI((n) => (n + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setI((n) => (n - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    const el = document.documentElement;
    if (el.requestFullscreen && !document.fullscreenElement) el.requestFullscreen().catch(() => {});
    return () => { if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {}); };
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === ' ') { e.preventDefault(); setPaused((p) => !p); }
    };
    window.addEventListener('keydown', onKey);
    const onFs = () => { if (!document.fullscreenElement) onClose(); };
    document.addEventListener('fullscreenchange', onFs);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('fullscreenchange', onFs); };
  }, [next, prev, onClose]);
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [paused]);
  useEffect(() => { if (tick && tick % seconds === 0) next(); }, [tick, seconds, next]);
  useEffect(() => { setTick(0); }, [i]);

  const slide = slides[i];
  const byKey = Object.fromEntries(data.tiles.map((t) => [t.key, t]));
  const tiles = slide.key === 'pinned' ? (data.prefs.pinned || []).map((k) => byKey[k]).filter(Boolean) : data.tiles.filter((t) => t.area === slide.key);
  const counts = data.tiles.reduce((acc, t) => ({ ...acc, [t.status]: (acc[t.status] || 0) + 1 }), {});

  return (
    <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: '#0d2238', color: 'var(--cream)' }} data-testid="wall-display" onDoubleClick={onClose}>
      <div className="h-1.5 w-full" style={{ background: 'rgba(255,255,255,0.1)' }}>
        <div className="h-full" style={{ width: `${Math.min(100, (tick % seconds) / seconds * 100)}%`, background: 'var(--gold)', transition: 'width 1s linear' }} data-testid="wall-progress" />
      </div>
      <header className="flex items-center justify-between px-8 py-5">
        <div>
          <div className="text-sm uppercase tracking-[0.3em] opacity-60">RAE Tasmania Sappers · Operations</div>
          <h1 className="font-title" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }} data-testid="wall-slide-title">{slide.label}</h1>
        </div>
        <div className="flex items-center gap-6 text-lg">
          {['red', 'amber', 'green'].map((s) => (
            <span key={s} className="inline-flex items-center gap-2"><span className="w-4 h-4 rounded-full" style={{ background: STATUS_COLOURS[s] }} /> {counts[s] || 0}</span>
          ))}
          <span className="opacity-60 text-base">{new Date(data.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </header>
      <main className="flex-1 px-8 pb-6 overflow-hidden">
        {slide.key === 'overview' ? (
          data.attention.length === 0
            ? <div className="h-full flex items-center justify-center font-title text-5xl" style={{ color: STATUS_COLOURS.green }} data-testid="wall-all-clear">All clear</div>
            : <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wall-attention">
              {data.attention.slice(0, 10).map((a) => (
                <li key={a.key} className="flex items-center gap-5 rounded-2xl px-6 py-4" style={{ background: 'rgba(255,255,255,0.06)', borderLeft: `8px solid ${STATUS_COLOURS[a.status]}` }}>
                  <AlertTriangle size={28} style={{ color: STATUS_COLOURS[a.status] }} />
                  <span className="font-title" style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}>{a.value}{a.unit}</span>
                  <span className="text-xl md:text-2xl">{a.label}</span>
                </li>
              ))}
            </ul>
        ) : (
          <div className={`grid gap-5 h-full ${tiles.length <= 4 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'}`}>
            {tiles.map((t) => <BigTile key={t.key} tile={t} />)}
          </div>
        )}
      </main>
      <footer className="flex items-center justify-between px-8 py-3 text-sm opacity-80">
        <div className="flex items-center gap-2">
          {slides.map((s, n) => <button key={s.key} onClick={() => setI(n)} className="w-2.5 h-2.5 rounded-full" style={{ background: n === i ? 'var(--gold)' : 'rgba(255,255,255,0.3)' }} aria-label={s.label} data-testid={`wall-dot-${s.key}`} />)}
          {slide.key === 'pinned' && <Pin size={14} className="ml-2" />}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prev} className="p-2 rounded hover:bg-white/10" data-testid="wall-prev"><ChevronLeft size={20} /></button>
          <button onClick={() => setPaused((p) => !p)} className="p-2 rounded hover:bg-white/10" data-testid="wall-pause">{paused ? <Play size={20} /> : <Pause size={20} />}</button>
          <button onClick={next} className="p-2 rounded hover:bg-white/10" data-testid="wall-next"><ChevronRight size={20} /></button>
          <span className="ml-3 opacity-70">{seconds}s per screen · Esc or double-click to exit</span>
          <button onClick={onClose} className="ml-3 p-2 rounded hover:bg-white/10" data-testid="wall-close"><X size={20} /></button>
        </div>
      </footer>
    </div>
  );
}
